import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import { FileText, ArrowLeft, Eye, RotateCcw, Search, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DOCUMENT_TYPES = [
  { value: 'ALL', label: 'All Types' },
  { value: 'SETTLEMENT', label: 'Settlement' },
  { value: 'WIP', label: 'WIP Posting' },
  { value: 'GOODS_ISSUE', label: 'Goods Issue' },
  { value: 'CONFIRMATION', label: 'Confirmation' },
];

export default function FIDocuments() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    project_id: 'ALL',
    document_type: 'ALL',
    fiscal_year: new Date().getFullYear()
  });
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showReversalDialog, setShowReversalDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [reversalData, setReversalData] = useState({
    reversal_date: new Date().toISOString().split('T')[0],
    reversal_reason: ''
  });

  useEffect(() => {
    fetchData();
    fetchProjects();
  // eslint-disable-next-line
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await psAPI.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.project_id && filters.project_id !== 'ALL') params.project_id = filters.project_id;
      if (filters.document_type && filters.document_type !== 'ALL') params.document_type = filters.document_type;
      if (filters.fiscal_year) params.fiscal_year = filters.fiscal_year;
      
      const response = await psAPI.getFIDocuments(params);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching FI documents:', error);
      toast.error('Failed to load FI documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchData();
  };

  const handleViewDocument = async (doc) => {
    try {
      const response = await psAPI.getFIDocument(doc.fi_document_id);
      setSelectedDoc(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load document details');
    }
  };

  const handleReverseClick = (doc) => {
    if (doc.reversed) {
      toast.error('Document is already reversed');
      return;
    }
    setSelectedDoc(doc);
    setReversalData({
      reversal_date: new Date().toISOString().split('T')[0],
      reversal_reason: ''
    });
    setShowReversalDialog(true);
  };

  const handleReversal = async () => {
    try {
      await psAPI.reverseFIDocument(
        selectedDoc.fi_document_id,
        reversalData.reversal_date,
        reversalData.reversal_reason || null
      );
      toast.success('Document reversed successfully');
      setShowReversalDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error reversing document:', error);
      toast.error(error.response?.data?.detail || 'Failed to reverse document');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="fi-documents-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/ps/integration')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">PS-FI Documents</h1>
              <p className="text-gray-500 mt-1">View and manage Finance Integration documents</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2 min-w-[200px]">
                <Label className="text-xs text-gray-500">Project</Label>
                <Select
                  value={filters.project_id}
                  onValueChange={(v) => setFilters({ ...filters, project_id: v })}
                >
                  <SelectTrigger data-testid="project-filter">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Projects</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.project_id} - {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 min-w-[150px]">
                <Label className="text-xs text-gray-500">Document Type</Label>
                <Select
                  value={filters.document_type}
                  onValueChange={(v) => setFilters({ ...filters, document_type: v })}
                >
                  <SelectTrigger data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 w-[100px]">
                <Label className="text-xs text-gray-500">Fiscal Year</Label>
                <Input
                  type="number"
                  value={filters.fiscal_year}
                  onChange={(e) => setFilters({ ...filters, fiscal_year: parseInt(e.target.value) || '' })}
                  data-testid="year-filter"
                />
              </div>

              <Button onClick={handleFilter} data-testid="filter-btn">
                <Filter className="h-4 w-4 mr-2" />
                Apply Filter
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Documents Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              FI Documents ({documents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No FI documents found</p>
                <p className="text-sm mt-1">Documents will appear here after posting</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium">Document #</th>
                      <th className="text-left p-3 font-medium">Type</th>
                      <th className="text-left p-3 font-medium">Project</th>
                      <th className="text-left p-3 font-medium">FY/Period</th>
                      <th className="text-right p-3 font-medium">Debit</th>
                      <th className="text-right p-3 font-medium">Credit</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Posting Date</th>
                      <th className="text-center p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.fi_document_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-mono text-orange-600">{doc.document_number}</td>
                        <td className="p-3">
                          <Badge variant="outline">{doc.document_type}</Badge>
                        </td>
                        <td className="p-3">{doc.project_name || doc.project_id}</td>
                        <td className="p-3 text-gray-500">
                          {doc.fiscal_year}/{String(doc.period).padStart(2, '0')}
                        </td>
                        <td className="p-3 text-right font-medium text-green-600">
                          ${doc.total_debit?.toLocaleString() || '0'}
                        </td>
                        <td className="p-3 text-right font-medium text-red-600">
                          ${doc.total_credit?.toLocaleString() || '0'}
                        </td>
                        <td className="p-3">
                          {doc.reversed ? (
                            <Badge className="bg-red-100 text-red-800">Reversed</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">Posted</Badge>
                          )}
                        </td>
                        <td className="p-3 text-gray-500">
                          {new Date(doc.posting_date).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDocument(doc)}
                              title="View Details"
                              data-testid={`view-btn-${doc.fi_document_id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {!doc.reversed && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReverseClick(doc)}
                                title="Reverse Document"
                                className="text-red-600 hover:text-red-700"
                                data-testid={`reverse-btn-${doc.fi_document_id}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Document Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>FI Document Details</DialogTitle>
            </DialogHeader>
            
            {selectedDoc && (
              <div className="space-y-4">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Document Number</p>
                    <p className="font-mono font-medium">{selectedDoc.document_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Type</p>
                    <Badge variant="outline">{selectedDoc.document_type}</Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Project</p>
                    <p className="font-medium">{selectedDoc.project_name || selectedDoc.project_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Company Code</p>
                    <p className="font-medium">{selectedDoc.company_code}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Fiscal Year / Period</p>
                    <p className="font-medium">{selectedDoc.fiscal_year} / {selectedDoc.period}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Posting Date</p>
                    <p className="font-medium">{new Date(selectedDoc.posting_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    {selectedDoc.reversed ? (
                      <Badge className="bg-red-100 text-red-800">Reversed</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800">Posted</Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-500">Created By</p>
                    <p className="font-medium">{selectedDoc.created_by || 'SYSTEM'}</p>
                  </div>
                </div>

                {/* Line Items */}
                <div className="border rounded-lg">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <h4 className="font-medium">Line Items</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2 font-medium">#</th>
                          <th className="text-left p-2 font-medium">GL Account</th>
                          <th className="text-right p-2 font-medium">Debit</th>
                          <th className="text-right p-2 font-medium">Credit</th>
                          <th className="text-left p-2 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedDoc.items || []).map((item, idx) => (
                          <tr key={item.line_item_id || item.item_id || `line-${idx}`} className="border-b">
                            <td className="p-2">{idx + 1}</td>
                            <td className="p-2 font-mono">{item.gl_account}</td>
                            <td className="p-2 text-right text-green-600">
                              {item.amount > 0 ? `$${item.amount.toLocaleString()}` : '-'}
                            </td>
                            <td className="p-2 text-right text-red-600">
                              {item.amount < 0 ? `$${Math.abs(item.amount).toLocaleString()}` : '-'}
                            </td>
                            <td className="p-2 text-gray-500">{item.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 font-medium">
                        <tr>
                          <td colSpan={2} className="p-2 text-right">Total:</td>
                          <td className="p-2 text-right text-green-600">
                            ${selectedDoc.total_debit?.toLocaleString() || '0'}
                          </td>
                          <td className="p-2 text-right text-red-600">
                            ${selectedDoc.total_credit?.toLocaleString() || '0'}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {selectedDoc.reversed && selectedDoc.reversal_document && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                    <p className="text-red-800">
                      <strong>Reversed by:</strong> {selectedDoc.reversal_document}
                    </p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reversal Dialog */}
        <Dialog open={showReversalDialog} onOpenChange={setShowReversalDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reverse FI Document</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <p className="font-medium">Are you sure you want to reverse this document?</p>
                <p className="mt-1">Document: {selectedDoc?.document_number}</p>
                <p>Amount: ${selectedDoc?.total_debit?.toLocaleString() || '0'}</p>
              </div>

              <div className="space-y-2">
                <Label>Reversal Date *</Label>
                <Input
                  type="date"
                  value={reversalData.reversal_date}
                  onChange={(e) => setReversalData({ ...reversalData, reversal_date: e.target.value })}
                  data-testid="reversal-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Reversal Reason</Label>
                <Input
                  value={reversalData.reversal_reason}
                  onChange={(e) => setReversalData({ ...reversalData, reversal_reason: e.target.value })}
                  placeholder="Enter reason for reversal"
                  data-testid="reversal-reason-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReversalDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReversal} data-testid="confirm-reversal-btn">
                Reverse Document
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
